import { forwardRef, useId, type KeyboardEvent, type MouseEvent } from 'react';
import ReactDatePicker, { type ReactDatePickerCustomHeaderProps } from 'react-datepicker';
import { de, enUS, ru, zhCN, type Locale } from 'date-fns/locale';
import { format } from 'date-fns';
import cn from 'classnames';
import 'react-datepicker/dist/react-datepicker.css';
import styles from './styles.module.scss';

const locales: Record<string, Locale> = {
    de,
    en: enUS,
    ru,
    zh: zhCN,
};

type CommonProps = {
    label?: string;
    placeholder?: string;
    className?: string;
    error?: string;
    disabled?: boolean;
    clearable?: boolean;
    minDate?: Date;
    maxDate?: Date;
    id?: string;
    locale?: 'de' | 'en' | 'ru' | 'zh' | Locale;
    labels?: Partial<DatePickerLabels>;
    monthYearSelectors?: boolean;
};

export type DatePickerLabels = {
    clear: string;
    previousMonth: string;
    nextMonth: string;
    chooseDay: string;
    unavailableDay: string;
    singlePlaceholder: string;
    rangePlaceholder: string;
    chooseMonth: string;
    chooseYear: string;
};

const defaultLabels: DatePickerLabels = {
    clear: 'Очистить дату',
    previousMonth: 'Предыдущий месяц',
    nextMonth: 'Следующий месяц',
    chooseDay: 'Выберите',
    unavailableDay: 'Недоступно',
    singlePlaceholder: 'ДД.ММ.ГГГГ',
    rangePlaceholder: 'ДД.ММ.ГГГГ — ДД.ММ.ГГГГ',
    chooseMonth: 'Выберите месяц',
    chooseYear: 'Выберите год',
};

export type SingleDatePickerProps = CommonProps & {
    mode?: 'single';
    value: Date | null;
    onChange: (value: Date | null) => void;
};

export type DateRange = [Date | null, Date | null];

export type RangeDatePickerProps = CommonProps & {
    mode: 'range';
    value: DateRange;
    onChange: (value: DateRange) => void;
};

export type DatePickerProps = SingleDatePickerProps | RangeDatePickerProps;

type DateInputProps = {
    value?: string;
    onClick?: () => void;
    onKeyDown?: (event: KeyboardEvent<HTMLButtonElement>) => void;
    label?: string;
    placeholder?: string;
    error?: string;
    disabled?: boolean;
    clearable?: boolean;
    hasValue?: boolean;
    onClear: () => void;
    id: string;
    clearLabel: string;
};

const CalendarIcon = () => (
    <svg aria-hidden="true" viewBox="0 0 24 24">
        <path d="M7 3v3M17 3v3M4 9h16M6 5h12a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z" />
    </svg>
);

const CloseIcon = () => (
    <svg aria-hidden="true" viewBox="0 0 24 24">
        <path d="m7 7 10 10M17 7 7 17" />
    </svg>
);

const ArrowIcon = ({ direction }: { direction: 'left' | 'right' }) => (
    <svg aria-hidden="true" className={direction === 'right' ? styles.arrowRight : undefined} viewBox="0 0 24 24">
        <path d="m15 18-6-6 6-6" />
    </svg>
);

const DateInput = forwardRef<HTMLButtonElement, DateInputProps>(
    (
        {
            value,
            onClick,
            onKeyDown,
            label,
            placeholder,
            error,
            disabled,
            clearable,
            hasValue,
            onClear,
            id,
            clearLabel,
        },
        ref
    ) => {
        const handleClear = (event: MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            event.stopPropagation();
            onClear();
        };

        return (
            <div className={cn(styles.inputGroup, { [styles.hasValue]: hasValue })}>
                {label && (
                    <label className={styles.label} htmlFor={id}>
                        {label}
                    </label>
                )}
                <div className={styles.inputWrap}>
                    <button
                        ref={ref}
                        id={id}
                        className={cn(styles.input, {
                            [styles.inputError]: !!error,
                            [styles.inputDisabled]: disabled,
                            [styles.inputEmpty]: !value,
                            [styles.inputWithClear]: clearable && hasValue && !disabled,
                        })}
                        type="button"
                        disabled={disabled}
                        aria-invalid={!!error}
                        aria-describedby={error ? `${id}-error` : undefined}
                        onClick={onClick}
                        onKeyDown={onKeyDown}
                    >
                        <span className={styles.calendarIcon}>
                            <CalendarIcon />
                        </span>
                        <span className={styles.value}>{value || placeholder}</span>
                    </button>
                    {clearable && hasValue && !disabled && (
                        <button className={styles.clear} type="button" aria-label={clearLabel} onClick={handleClear}>
                            <CloseIcon />
                        </button>
                    )}
                </div>
                {error && (
                    <span id={`${id}-error`} className={styles.error} role="alert">
                        {error}
                    </span>
                )}
            </div>
        );
    }
);

DateInput.displayName = 'DateInput';

const Header = ({
    date,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
    changeMonth,
    changeYear,
    locale,
    previousLabel,
    nextLabel,
    monthYearSelectors,
    minDate,
    maxDate,
    chooseMonthLabel,
    chooseYearLabel,
}: ReactDatePickerCustomHeaderProps & {
    locale: Locale;
    previousLabel: string;
    nextLabel: string;
    monthYearSelectors: boolean;
    minDate?: Date;
    maxDate?: Date;
    chooseMonthLabel: string;
    chooseYearLabel: string;
}) => (
    <div className={styles.header}>
        <button type="button" aria-label={previousLabel} disabled={prevMonthButtonDisabled} onClick={decreaseMonth}>
            <ArrowIcon direction="left" />
        </button>
        {monthYearSelectors ? (
            <div className={styles.monthYearSelectors}>
                <select
                    aria-label={chooseMonthLabel}
                    value={date.getMonth()}
                    onChange={(event) => changeMonth(Number(event.target.value))}
                >
                    {Array.from({ length: 12 }, (_, month) => (
                        <option key={month} value={month}>
                            {format(new Date(2020, month, 1), 'LLLL', { locale })}
                        </option>
                    ))}
                </select>
                <select
                    aria-label={chooseYearLabel}
                    value={date.getFullYear()}
                    onChange={(event) => changeYear(Number(event.target.value))}
                >
                    {Array.from(
                        {
                            length:
                                (maxDate?.getFullYear() ?? new Date().getFullYear()) -
                                (minDate?.getFullYear() ?? new Date().getFullYear() - 120) +
                                1,
                        },
                        (_, index) => (maxDate?.getFullYear() ?? new Date().getFullYear()) - index
                    ).map((year) => (
                        <option key={year} value={year}>
                            {year}
                        </option>
                    ))}
                </select>
            </div>
        ) : (
            <strong aria-live="polite">{format(date, 'LLLL yyyy', { locale })}</strong>
        )}
        <button type="button" aria-label={nextLabel} disabled={nextMonthButtonDisabled} onClick={increaseMonth}>
            <ArrowIcon direction="right" />
        </button>
    </div>
);

export const DatePicker = (props: DatePickerProps) => {
    const {
        label,
        placeholder,
        className,
        error,
        disabled = false,
        clearable = true,
        minDate,
        maxDate,
        labels: labelsOverride,
        monthYearSelectors = false,
    } = props;
    const labels = { ...defaultLabels, ...labelsOverride };
    const generatedId = useId();
    const id = props.id ?? generatedId;
    const locale = typeof props.locale === 'string' ? locales[props.locale] : (props.locale ?? ru);
    const isRange = props.mode === 'range';
    const startDate = isRange ? props.value[0] : props.value;
    const endDate = isRange ? props.value[1] : null;
    const hasValue = Boolean(startDate || endDate);
    const handleClear = () => {
        if (props.mode === 'range') {
            props.onChange([null, null]);
        } else {
            props.onChange(null);
        }
    };

    const input = (
        <DateInput
            id={id}
            label={label}
            placeholder={placeholder ?? (isRange ? labels.rangePlaceholder : labels.singlePlaceholder)}
            error={error}
            disabled={disabled}
            clearable={clearable}
            hasValue={hasValue}
            onClear={handleClear}
            clearLabel={labels.clear}
        />
    );

    const commonProps = {
        id,
        customInput: input,
        calendarClassName: styles.calendar,
        popperClassName: styles.popper,
        dayClassName: () => styles.day,
        weekDayClassName: () => styles.weekDay,
        locale,
        dateFormat: 'dd.MM.yyyy',
        rangeSeparator: ' — ',
        chooseDayAriaLabelPrefix: labels.chooseDay,
        disabledDayAriaLabelPrefix: labels.unavailableDay,
        calendarStartDay: 1 as const,
        disabled,
        minDate,
        maxDate,
        showPopperArrow: false,
        shouldCloseOnSelect: true,
        renderCustomHeader: (headerProps: ReactDatePickerCustomHeaderProps) => (
            <Header
                {...headerProps}
                locale={locale}
                previousLabel={labels.previousMonth}
                nextLabel={labels.nextMonth}
                monthYearSelectors={monthYearSelectors}
                minDate={minDate}
                maxDate={maxDate}
                chooseMonthLabel={labels.chooseMonth}
                chooseYearLabel={labels.chooseYear}
            />
        ),
    };

    return (
        <div className={cn(styles.root, className)}>
            {isRange ? (
                <ReactDatePicker
                    {...commonProps}
                    selectsRange
                    allowSameDay
                    startDate={startDate}
                    endDate={endDate}
                    onChange={(dates) => props.onChange(dates)}
                />
            ) : (
                <ReactDatePicker {...commonProps} selected={startDate} onChange={(date) => props.onChange(date)} />
            )}
        </div>
    );
};
